import React, { useState, useEffect } from 'react';
import { Menu, X, Facebook, Phone, MapPin, Instagram } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const menuItems = [
    { label: 'Accueil', href: '#accueil' },
    { label: 'Présentation', href: '#presentation' },
    { label: 'À propos', href: '#about' },
    { label: 'Circuits', href: '#circuits' },
    { label: 'Témoignages', href: '#testimonials' },
    { label: 'Contact', href: '#contact' },
  ];

  const handlePhoneClick = () => {
    if (!navigator.userAgent.match(/(iPhone|Android|Mobile)/i)) {
      const phoneNumber = "+33627495066";
      const modal = document.createElement('div');
      modal.innerHTML = `
        <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onclick="this.remove()">
          <div class="bg-white p-8 rounded-lg shadow-xl text-center" onclick="event.stopPropagation()">
            <h3 class="text-2xl font-bold mb-4">Appelez-nous</h3>
            <p class="text-xl mb-4">${phoneNumber}</p>
            <button class="text-blue-600 hover:text-blue-800" onclick="this.closest('.fixed').remove()">Fermer</button>
          </div>
        </div>
      `;
      document.body.appendChild(modal);
    }
  };

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${
      scrolled ? 'bg-white shadow-md py-2' : 'bg-transparent backdrop-blur-sm py-4'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          <div className={`flex-shrink-0 font-semibold text-base sm:text-xl ${scrolled ? 'text-blue-900' : 'text-white'} flex items-center`}>
            <MapPin className={`w-5 h-5 mr-2 ${scrolled ? 'text-blue-900' : 'text-blue-300'}`} />
            <span className="hidden sm:inline">Rendez-vous Sans Frontières</span>
            <span className="sm:hidden">RVSF</span>
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center">
            <div className="flex items-center space-x-1">
              {menuItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    scrolled 
                      ? 'text-gray-700 hover:text-blue-900 hover:bg-blue-50' 
                      : 'text-white hover:text-blue-200 hover:bg-white/10'
                  }`}
                >
                  {item.label}
                </a>
              ))}
            </div>
            
            <div className="ml-6 flex items-center space-x-3">
              <a
                href="https://www.instagram.com/rendez_vous_sans_frontieres/"
                target="_blank"
                rel="noopener noreferrer"
                className={`transition-colors p-2 rounded-full ${
                  scrolled 
                    ? 'text-blue-900 hover:text-blue-700 hover:bg-blue-50' 
                    : 'text-white hover:text-blue-200 hover:bg-white/10'
                }`}
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>

              <a
                href="https://www.facebook.com/profile.php?id=61555043181014"
                target="_blank"
                rel="noopener noreferrer"
                className={`transition-colors p-2 rounded-full ${
                  scrolled 
                    ? 'text-blue-900 hover:text-blue-700 hover:bg-blue-50' 
                    : 'text-white hover:text-blue-200 hover:bg-white/10'
                }`}
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>

              <a
                href="https://g.page/r/CWcll2hdlZq6EAE"
                target="_blank"
                rel="noopener noreferrer"
                className={`transition-colors p-2 rounded-full ${
                  scrolled 
                    ? 'text-blue-900 hover:text-blue-700 hover:bg-blue-50' 
                    : 'text-white hover:text-blue-200 hover:bg-white/10'
                }`}
                aria-label="Google"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"/>
                </svg>
              </a>
              
              <a
                href="tel:+33627495066"
                onClick={handlePhoneClick}
                className={`hidden sm:flex px-4 py-2 rounded-md text-sm font-medium transition-colors items-center ${
                  scrolled
                    ? 'bg-blue-900 text-white hover:bg-blue-800'
                    : 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 border border-white/50'
                }`}
              >
                <Phone size={16} className="mr-2" />
                06 27 49 50 66
              </a>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-2">
            <a
              href="tel:+33627495066"
              onClick={handlePhoneClick}
              className={`transition-colors p-2 rounded-full ${
                scrolled 
                  ? 'text-blue-900 hover:bg-blue-50' 
                  : 'text-white hover:bg-white/10'
              }`}
              aria-label="Appeler"
            >
              <Phone size={20} />
            </a>
            
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`inline-flex items-center justify-center p-2 rounded-md focus:outline-none ${
                scrolled 
                  ? 'text-gray-700 hover:text-blue-900 hover:bg-blue-50' 
                  : 'text-white hover:text-blue-200 hover:bg-white/10'
              }`}
              aria-label="Menu principal"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden absolute w-full bg-white shadow-lg rounded-b-lg">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {menuItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="text-gray-700 hover:text-blue-900 hover:bg-blue-50 block px-3 py-2 rounded-md text-base font-medium transition-colors"
                onClick={() => setIsOpen(false)}
              >
                {item.label}
              </a>
            ))}
            
            <div className="flex items-center justify-center space-x-3 px-3 py-4 border-t border-gray-100">
              <a
                href="https://www.instagram.com/rendez_vous_sans_frontieres/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-900 hover:text-blue-700 hover:bg-blue-50 p-2 rounded-full transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>

              <a
                href="https://www.facebook.com/profile.php?id=61555043181014"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-900 hover:text-blue-700 hover:bg-blue-50 p-2 rounded-full transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>

              <a
                href="https://g.page/r/CWcll2hdlZq6EAE"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-900 hover:text-blue-700 hover:bg-blue-50 p-2 rounded-full transition-colors"
                aria-label="Google"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;